import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

// Simple session ID generator (no uuid dependency needed)
const getSessionId = () => {
  let id = localStorage.getItem('ce_session');
  if (!id) {
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    id = 'guest_' + Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem('ce_session', id);
  }
  return id;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], itemCount: 0, subtotal: 0 });
  const [loading, setLoading] = useState(false);

  const getHeaders = () => {
    if (!isAuthenticated) {
      return { 'x-session-id': getSessionId() };
    }
    return {};
  };

  const EMPTY_CART = { items: [], itemCount: 0, subtotal: 0 };

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/cart', { headers: getHeaders() });
      // Guard: never set cart to undefined/null — keep the empty default instead
      setCart(res.data?.data || EMPTY_CART);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching cart:', error);
      // On network failure keep whatever cart state we already have
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1, variant = null, wireOptions = null) => {
    try {
      const payload = { productId, quantity };
      // If a variant (size/color with pricing) was selected, pass it so the
      // backend can store the correct price/label in the cart line item.
      if (variant) {
        payload.variant = {
          type: variant.type,
          label: variant.label,
          sizeLabel: variant.sizeLabel || null,
          colorLabel: variant.colorLabel || null,
          sellingPrice: variant.sellingPrice,
          mrp: variant.mrp,
          sku: variant.sku,
        };
      }
      // Wire/cable products: pass buying mode and coil quantity
      if (wireOptions) {
        payload.buyingMode = wireOptions.buyingMode;
        payload.coilQuantity = wireOptions.coilQuantity || 0;
      }
      const res = await api.post('/cart/add', payload, { headers: getHeaders() });
      await fetchCart();
      return true;
    } catch (error) {
      throw error;
    }
  };

  const updateQuantity = async (productId, quantity, variantLabel = null) => {
    try {
      const payload = { productId, quantity };
      if (variantLabel) payload.variantLabel = variantLabel;
      await api.put('/cart/update', payload, { headers: getHeaders() });
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  const removeFromCart = async (productId, variantLabel = null) => {
    try {
      const params = variantLabel ? `?variantLabel=${encodeURIComponent(variantLabel)}` : '';
      await api.delete(`/cart/remove/${productId}${params}`, { headers: getHeaders() });
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      await api.delete('/cart/clear', { headers: getHeaders() });
      setCart({ items: [], itemCount: 0, subtotal: 0 });
    } catch (error) {
      throw error;
    }
  };

  return (
    <CartContext.Provider value={{
      cart, loading, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart,
    }}>
      {children}
    </CartContext.Provider>
  );
};
